import { inject, Injectable } from '@angular/core';
import { AsyncValidatorFn, ValidatorFn, Validators } from '@angular/forms';
import { LeaseStatusEnum } from '@app/core/enums/asset-occupant/lease-status-enum';
import { ConditionOperator } from '@app/core/enums/condition-operator.enum';
import { ContractStateEnum } from '@app/core/enums/contract/contract-state.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { ValidatorType } from '@app/core/enums/validator-type.enum';
import { Asset } from '@app/core/model/entities/asset/asset';
import { Space } from '@app/core/model/entities/asset/space';
import { Document } from '@app/core/model/entities/document/document';
import { Entity } from '@app/core/model/entities/entity';
import { Work } from '@app/core/model/entities/works/work';
import { Condition, FieldConfig, isValidatorFieldDependant } from '@app/core/model/other/field-config';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { SpacesService } from '@app/features/main/views/organization-spaces/spaces.service';
import { ProjectsService } from '@app/features/main/views/projects/projects.service';
import { WorksService } from '@app/features/main/views/works/works.service';
import { FormStateService } from '@app/shared/components/form-builder/form-state.service';
import { getValue, validateCondition } from '@app/shared/extra/utils';
import { ExtraValidators } from '@app/shared/validators/extra-validators.module';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { concatMap, defaultIfEmpty, EMPTY, every, from, Observable } from 'rxjs';

@Injectable()
export class ValidationService {
  private readonly formStateService = inject(FormStateService);
  private readonly apiService = inject(ApiService);
  private readonly projectsService = inject(ProjectsService);
  private readonly spacesService = inject(SpacesService);
  private readonly worksService = inject(WorksService);
  private readonly appManager = inject(AppManager);

  /**
   * Get the validator function corresponding to a validator type.
   * @param validator the validator config
   * @param entity available if information should be extracted from the entity to configure the validator
   * @param data Addition data that can be passed to the validator when applicable.
   * @return Validator function.
   */
  public getValidator(validator: FieldValidator, entity?: Entity, data?: { [key: string]: any }): ValidatorFn {
    switch (validator.type) {
      case ValidatorType.MAX_LENGTH:
        return Validators.maxLength(validator.definition);
      case ValidatorType.MIN_VALUE:
        return ExtraValidators.gte(validator.definition);
      case ValidatorType.MAX_VALUE:
        return ExtraValidators.lte(validator.definition);
      case ValidatorType.INTEGER:
        return ExtraValidators.integer;
      case ValidatorType.MAX_NOW:
        return ExtraValidators.maxNow;
      case ValidatorType.YEAR:
        return ExtraValidators.year;
      case ValidatorType.DATE:
        return ExtraValidators.dateISO;
      case ValidatorType.DECIMAL:
        return ExtraValidators.decimal;
      case ValidatorType.NOT_IN_ARRAY:
        return ExtraValidators.notInArray(validator.definition);
      case ValidatorType.IN_ARRAY:
        return ExtraValidators.inArray(validator.definition);
      case ValidatorType.AFTER_DATE:
        return ExtraValidators.afterDate(validator.definition);
      case ValidatorType.BEFORE_DATE:
        return ExtraValidators.beforeDate(validator.definition);
      case ValidatorType.REQUIRED:
        return Validators.required;
      case ValidatorType.END_DATE_IS_BEFORE_TODAY:
        if (entity.objectType === EntityTypeEnum.LEASE) {
          return ExtraValidators.endDateIsBeforeToday(
            () => this.formStateService.getPath(['leaseDates', 'value', 'endDate']),
            LeaseStatusEnum.LEASE_ACTIVE
          );
        } else if (entity.objectType === EntityTypeEnum.CONTRACT) {
          return ExtraValidators.endDateIsBeforeToday(
            () => this.formStateService.getPath(['contractDates', 'value', 'endDate']),
            ContractStateEnum.CONTRACT_ACTIVE
          );
        } else {
          // Type of entity is not handled for this validator
          return Validators.nullValidator;
        }
      case ValidatorType.END_DATE_IS_AFTER_TODAY:
        if (entity.objectType === EntityTypeEnum.LEASE) {
          return ExtraValidators.endDateIsAfterToday(
            () => this.formStateService.getPath(['leaseDates', 'value', 'endDate']),
            LeaseStatusEnum.LEASE_EXPIRED
          );
        } else if (entity.objectType === EntityTypeEnum.CONTRACT) {
          return ExtraValidators.endDateIsAfterToday(
            () => this.formStateService.getPath(['contractDates', 'value', 'endDate']),
            ContractStateEnum.CONTRACT_EXPIRED
          );
        } else {
          // Type of entity is not handled for this validator
          return Validators.nullValidator;
        }
      case ValidatorType.END_DATE_IS_UNDEFINED:
        if (entity.objectType === EntityTypeEnum.LEASE) {
          return ExtraValidators.endDateIsUndefined(
            () => this.formStateService.getPath(['leaseDates', 'value', 'endDate']),
            LeaseStatusEnum.LEASE_EXPIRED
          );
        } else if (entity.objectType === EntityTypeEnum.CONTRACT) {
          return ExtraValidators.endDateIsUndefined(
            () => this.formStateService.getPath(['contractDates', 'value', 'endDate']),
            ContractStateEnum.CONTRACT_EXPIRED
          );
        } else {
          // Type of entity is not handled for this validator
          return Validators.nullValidator;
        }
      case ValidatorType.REALISATION_DATE_IS_BEFORE_TODAY:
        return ExtraValidators.realisationDateIsBeforeToday(
          () => this.formStateService.getPath(['realisationDate', 'value', 'realisationDate']),
          () => this.formStateService.getPath(['periodicity', 'value', 'periodicity'])
        );
      case ValidatorType.REALISATION_DATE_IS_AFTER_TODAY:
        return ExtraValidators.realisationDateIsAfterToday(
          () => this.formStateService.getPath(['realisationDate', 'value', 'realisationDate']),
          () => this.formStateService.getPath(['periodicity', 'value', 'periodicity'])
        );
      case ValidatorType.RENEW_DATE_IS_AFTER_TODAY:
        return ExtraValidators.renewDateIsAfterToday(
          () => this.formStateService.getPath(['realisationDate', 'value', 'realisationDate']),
          () => this.formStateService.getPath(['periodicity', 'value', 'periodicity'])
        );
      case ValidatorType.RENEW_DATE_IS_UNDEFINED:
        return ExtraValidators.renewDateIsUndefined(
          () => this.formStateService.getPath(['realisationDate', 'value', 'realisationDate']),
          () => this.formStateService.getPath(['periodicity', 'value', 'periodicity'])
        );
      case ValidatorType.INCOHERENT_SPACES:
        return ExtraValidators.checkSpaces;
      case ValidatorType.GTE_TOTAL_EXPENSES:
      case ValidatorType.LTE_TOTAL_COSTS:
        return ExtraValidators.checkBudgetBalance(this.formStateService);
      case ValidatorType.MAX_LENGTH_FILE_NAME:
        return ExtraValidators.maxLengthFileName(
          validator.definition,
          entity instanceof Document ? entity?.getExtension() : ''
        );
      case ValidatorType.REGEX:
        return ExtraValidators.regex(validator.definition);
      case ValidatorType.EMAIL:
        return Validators.email;
      case ValidatorType.DISTINCT:
        return ExtraValidators.distinct(data?.values ?? []);
      case ValidatorType.DISTINCT_CRITERIA:
        return ExtraValidators.distinctCriteria();
      default:
        return Validators.nullValidator;
    }
  }

  /**
   * Get the validator corresponding to the validator's code name
   * @param validator the validator configuration
   * @param entity available if information should be extracted from the entity to configure the validator
   * @param fieldPath the validation target field's path
   */
  public getAsyncValidator(validator: FieldValidator,
                           entity?: Entity,
                           fieldPath?: string[]): AsyncValidatorFn {
    switch (validator.type) {
      case ValidatorType.UNIQUE:
        return ExtraValidators.isValueTaken(
          this.apiService,
          EntityTypeEnum[entity.objectType],
          fieldPath?.join('.'),
          String(entity.id),
          {
            organizationId: this.appManager.currentOrganization.id,
            [validator.definition]: getValue(entity, [validator.definition])
          }
        );
      case ValidatorType.GTE_SPACE_CHILDREN_PROPERTY:
        return ExtraValidators.gteSpaceChildrenProperty(this.spacesService, fieldPath?.last(), entity as Space);
      case ValidatorType.LTE_SPACE_PARENT_PROPERTY:
        return ExtraValidators.lteSpaceParentProperty(this.spacesService, fieldPath?.last(), entity as Space);
      case ValidatorType.ASSET_VALUE_GTE_SPACE_CHILDREN_PROPERTY:
        return ExtraValidators.assetValueGteSpaceChildrenProperty(
          this.spacesService,
          validator.definition,
          entity as Asset
        );
      case ValidatorType.IS_SPACE_MOVE_ALLOWED:
        return ExtraValidators.isSpaceMoveAllowed(this.spacesService, entity as Space);
      case ValidatorType.IS_ASSET_CHANGE_ALLOWED:
        return ExtraValidators.canChangeWorkAsset(this.worksService, entity as Work);
      default:
        return null;
    }
  }

  /**
   * Used to validate that a field can be edited
   * @param fieldConfig Configuration of the field to check
   * @param entity Used to check editable conditions depending on entity
   * @return True if the field can be editable, false if it is read only
   */
  public isFieldEditable(fieldConfig: FieldConfig, entity?: Entity): boolean {
    // No config found, can not edit.
    if (!fieldConfig) return false;

    // Field is not editable if computed and does not have an ALWAYS condition to otherwise allow it
    const alwaysAllowEditing = !!fieldConfig.conditionsToEdit.singleOrNull(cond => cond.operator === ConditionOperator.ALWAYS);
    if (fieldConfig.computed && !alwaysAllowEditing) return false;

    // Evaluate conditions to edit
    return this.validateConditions(entity, fieldConfig.conditionsToEdit);
  }

  /**
   * Iterate over conditions to validate them using the entity being edited
   * @param entity entity in which properties might be looked up
   * @param conditions list of conditions to check
   * @return True if conditions are met, false otherwise
   */
  public validateConditions(entity: Entity, conditions: Condition[] = []): boolean {
    // If no conditions, there is nothing to validate
    if (conditions.length === 0) return true;
    return conditions.every((condition) => {
      const valueFromEntity = condition.field ? getValue(entity, condition.field?.split('.')) : null;
      return validateCondition(valueFromEntity, condition);
    });
  }

  /**
   * Check and evaluate asynchronous conditions to edit. If true, the field is enabled and can be edited.
   * If false, the field must be in read only mode. If no conditions exist, return true.
   * @param entity The current entity.
   * @param conditions The list of conditions to check asynchronously.
   * @return Observable emitting true if the field is editable, false otherwise.
   */
  public validateAsyncConditions(entity: Entity, conditions: Condition[]): Observable<boolean> {
    return from(conditions)
      .pipe(
        // Check the async validator condition.
        concatMap((condition) => this.validateAsyncCondition(entity, condition)),
        // Check if all the results are true.
        every(result => result),
        // True by default (meaning that the field can be editable).
        defaultIfEmpty(true)
      );
  }

  /**
   * Get the async precondition validator corresponding to the validator's code name.
   * @param entity The entity related to the field.
   * @param condition The condition to check asynchronously.
   * @return Observable emitting true or false by the validator, empty otherwise.
   * @protected
   */
  protected validateAsyncCondition(entity: Entity, condition: Condition): Observable<boolean> {
    switch (condition.operator) {
      case ConditionOperator.PROJECT_HAS_NO_INACCESSIBLE_ASSETS:
        return this.projectsService.projectHasNoInaccessibleAssets(entity.id);
      default:
        return EMPTY;
    }
  }

  /**
   * Get the type which replace the validator type of a validator which depends on another field in the entity.
   * @param type Type of the validator which should be converted.
   * @return ValidatorType Type the validator should be converted into, or null if there is no need to convert it.
   */
  public getFieldDependantValidatorType(type: ValidatorType): ValidatorType {
    switch (type) {
      case ValidatorType.AFTER_OTHER_DATE:
        return ValidatorType.AFTER_DATE;
      case ValidatorType.BEFORE_OTHER_DATE:
        return ValidatorType.BEFORE_DATE;
      default:
        return null;
    }
  }

  /**
   * Check if a validator depends on another field of the entity and, if so, convert it into the corresponding validator.
   * @param validatorToConvert Validator depending on another field of the entity.
   * @param definition Value to set as the definition of the converted validator.
   * @return FieldValidator Return the corresponding validator, or the validator itself if no replacement was needed.
   */
  public convertFieldDependantValidator(validatorToConvert: FieldValidator, definition: any): FieldValidator {
    if (isValidatorFieldDependant(validatorToConvert.type)) {
      const fieldValidator = new FieldValidator(
        this.getFieldDependantValidatorType(validatorToConvert.type),
        definition
      );
      fieldValidator.ref = validatorToConvert.definitionPath.last();
      return fieldValidator;
    } else {
      return validatorToConvert;
    }
  }
}
